import { WarningMessages } from 'constants/Warning.constants';

import { selectVariables } from '@polyai/stream/store/reducers/variables/selectors';
import type { VariableReference } from '@polyai/stream/store/reducers/variables/types';
import type { References } from '@polyai/stream/store/references';
import { VariableIcon } from '@polyai/ui/components/atoms/Icons';
import { PopupMenuItem } from '@polyai/ui/components/molecules/PopupMenu';

import { VARIABLE_PREFIX } from 'components/molecules/LexicalEditor/features/action/constants';
import type {
  ActionTagContentProps,
  ActionTypeInput,
  ActionTypeOutput,
} from 'components/molecules/LexicalEditor/features/action/types';
import { VariableEditPopup } from 'components/molecules/LexicalEditor/plugins/ActionPicker/components/VariablePopup';
import { useActionsContext } from 'components/molecules/RichTextInputV2';
import { useAppSelector } from 'hooks/reduxHooks';
import { useBoolean } from 'hooks/useBoolean';

const checkIfLastReference = (references: References<VariableReference>) => {
  const totalReferences = Object.values(references).reduce((total, group) => {
    return total + Object.keys(group ?? {}).length;
  }, 0);

  return totalReferences === 1 || totalReferences === 0;
};

const VariableTagContent = ({
  id,
  remove,
  closePopup,
}: ActionTagContentProps) => {
  const variables = useAppSelector(selectVariables);
  const variable = variables?.find(v => v.id === id);
  const {
    value: isEditModalOpen,
    setTrue: openEditModal,
    setFalse: closeEditModal,
  } = useBoolean(false);

  const isRemove = !!variable && !checkIfLastReference(variable.references);
  const buttonText = isRemove ? 'Remove' : 'Delete';

  return (
    <>
      {variable && <PopupMenuItem onClick={openEditModal}>Edit</PopupMenuItem>}
      <PopupMenuItem isDelete onClick={remove}>
        {buttonText}
      </PopupMenuItem>

      {isEditModalOpen && (
        <VariableEditPopup
          variableId={id}
          onClose={() => {
            closeEditModal();
            closePopup?.();
          }}
        />
      )}
    </>
  );
};

export const useVariableActionType = ({
  id,
}: ActionTypeInput): ActionTypeOutput => {
  const { isVariablesEnabled } = useActionsContext();
  const variables = useAppSelector(selectVariables);
  const variable = variables?.find(v => v.id === id);

  const warning = !variable ? WarningMessages.VARIABLE_NOT_EXIST : undefined;

  return {
    Content: VariableTagContent,
    Icon: VariableIcon,
    name: variable?.name ?? id,
    warning,
    prefix: VARIABLE_PREFIX,
    isEnabled: isVariablesEnabled,
    isLoading: false,
  };
};
